<script setup lang="ts">
const props = defineProps<{
  parsedCustomData: {
    descricoes: any[];
    termos_condicoes: any[];
  };
  tableHeight?: number;
}>();

const { isDesktop } = useLayout();

const scrollContainer = ref<HTMLElement>();
const showScrollIndicator = ref(false);

const checkScrollable = () => {
  if (scrollContainer.value) {
    const { scrollHeight, clientHeight, scrollTop } = scrollContainer.value;
    const isScrollable = scrollHeight > clientHeight;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    showScrollIndicator.value = isScrollable && !isNearBottom;
  }
};

onMounted(() => {
  // Aguardar próximo tick para garantir que o DOM esteja renderizado
  nextTick(() => {
    checkScrollable();
    if (scrollContainer.value) {
      scrollContainer.value.addEventListener('scroll', checkScrollable);
      // Para dispositivos móveis, também escutar eventos de touch
      scrollContainer.value.addEventListener('touchmove', checkScrollable);
      scrollContainer.value.addEventListener('touchend', checkScrollable);
    }
  });

  // Verificar novamente após um pequeno delay para garantir que o conteúdo carregou
  setTimeout(() => {
    checkScrollable();
  }, 100);

  // Escutar resize para recalcular em mudanças de viewport
  window.addEventListener('resize', checkScrollable);
});

// Observar mudanças no conteúdo dos termos para recalcular
watch(
  () => props.parsedCustomData.termos_condicoes,
  () => {
    nextTick(() => {
      setTimeout(() => {
        checkScrollable();
      }, 50);
    });
  },
  { deep: true },
);

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', checkScrollable);
    scrollContainer.value.removeEventListener('touchmove', checkScrollable);
    scrollContainer.value.removeEventListener('touchend', checkScrollable);
  }
  window.removeEventListener('resize', checkScrollable);
});
</script>

<template>
  <div class="flex-1 flex flex-col items-center min-w-[356px]">
    <span
      v-if="isDesktop"
      class="font-bold text-lg text-white mb-4"
    >
      Termos e Condições
    </span>
    <div
      class="[background:#121722] backdrop-blur-[11.052631378173828px] p-5 pb-7 border-[1.105px] border-solid border-[#1D2329] rounded-lg text-texts relative overflow-hidden"
      :style="{ height: props.tableHeight ? `${props.tableHeight}px` : 'auto' }"
    >
      <div
        ref="scrollContainer"
        class="overflow-auto flex flex-col gap-1"
        :style="{
          height: props.tableHeight ? `${props.tableHeight - 40}px` : 'auto',
        }"
      >
        <div
          v-for="(term, index) in props.parsedCustomData.termos_condicoes"
          :key="`term-${index}`"
          class="text-white text-xs font-semibold leading-4 pr-2"
          v-html="term.replace(/\n/g, '<br />')"
        ></div>
      </div>

      <!-- Indicador de mais conteúdo -->
      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 transform translate-y-2"
        leave-active-class="transition-all duration-300 ease-in"
        leave-to-class="opacity-0 transform translate-y-2"
      >
        <div
          v-if="showScrollIndicator"
          class="absolute bottom-0 left-0 right-0 pointer-events-none rounded-b-[6px] overflow-hidden"
        >
          <!-- Gradiente de fade -->
          <div
            class="h-16 bg-gradient-to-t from-[#121722] via-[#121722]/80 to-transparent rounded-b-[6px]"
          ></div>

          <!-- Indicador visual -->
          <div
            class="absolute bottom-1 left-1/2 transform -translate-x-1/2 z-10"
          >
            <div
              class="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap"
              style="
                animation: gentle-bounce 2s ease-in-out infinite;
                min-width: fit-content;
              "
            >
              <span>Role para ver mais</span>
              <svg
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
@keyframes gentle-bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-3px);
  }
  60% {
    transform: translateY(-2px);
  }
}
</style>
